import React, { forwardRef, useMemo } from 'react';

import { TABLE_MODES, getModeHeights } from '../../../constants/agGridStyles';
import {
  AG_GRID_FIT_MODES,
  AG_GRID_SIZING,
  calculateAgGridTableSize,
} from '../../../utils/agGridSizing';
import AgGridWrapper from '../../tables/AgGridWrapper';
import AnalyticsTablePanel from './AnalyticsTablePanel';

export const WORKSPACE_GRID_KINDS = {
  SIGNAL_TABLE: 'signalTable',
  COMPACT_SIGNAL_TABLE: 'compactSignalTable',
  LEADERSHIP_TABLE: 'leadershipTable',
  JOB_BOARD: 'jobBoard',
  METRICS_TABLE: 'metricsTable',
};

export const WORKSPACE_GRID_FILL_MODES = {
  AUTO: 'auto',
  ALWAYS: 'always',
  NEVER: 'never',
};

export const WORKSPACE_GRID_PANEL_WIDTH_MODES = {
  CONTENT: 'content',
  CONTAINER: 'container',
};

const DEFAULT_CONTENT_PADDING = {
  boxSizing: 'border-box',
  p: { xs: 0.5, md: 0.75 },
};

export const WORKSPACE_GRID_MOBILE_FRAME_HEIGHTS = {
  FULL_PAGE_TABLE: {
    xs: 'clamp(420px, calc(100svh - 220px), 720px)',
    sm: 'clamp(480px, calc(100svh - 220px), 760px)',
  },
  DASHBOARD_TABLE: {
    xs: 'clamp(360px, calc(55svh - 120px), 620px)',
    sm: 'clamp(400px, calc(55svh - 120px), 660px)',
  },
};

const LARGE_TABLE_FILL_MIN_FRAME_HEIGHT = {
  xs: 'clamp(420px, calc(100svh - 220px), 720px)',
  sm: 'clamp(480px, calc(100svh - 220px), 760px)',
};

const resolveFillMinFrameHeight = (height, desktopHeight) => {
  if (!height || typeof height !== 'object' || Array.isArray(height)) {
    return height ?? desktopHeight;
  }

  return {
    ...height,
    md: height.md ?? desktopHeight,
  };
};

const KIND_CONFIG = {
  [WORKSPACE_GRID_KINDS.SIGNAL_TABLE]: {
    fitMode: AG_GRID_FIT_MODES.COLUMNS,
    minWidth: 1,
    maxWidth: 1120,
    minHeight: 96,
    maxHeight: 340,
    legacyMaxTableHeight: 360,
    legacyMinFrameHeight: 260,
  },
  [WORKSPACE_GRID_KINDS.COMPACT_SIGNAL_TABLE]: {
    fitMode: AG_GRID_FIT_MODES.COLUMNS,
    minWidth: 1,
    maxWidth: 980,
    minHeight: 80,
    maxHeight: 260,
    legacyMaxTableHeight: 360,
    legacyMinFrameHeight: 260,
  },
  [WORKSPACE_GRID_KINDS.LEADERSHIP_TABLE]: {
    fitMode: AG_GRID_FIT_MODES.COLUMNS,
    minWidth: 500,
    maxWidth: 1200,
    minHeight: 96,
    maxHeight: 620,
    fillMinFrameHeight: LARGE_TABLE_FILL_MIN_FRAME_HEIGHT,
    legacyMaxTableHeight: 620,
    legacyMinFrameHeight: 320,
  },
  [WORKSPACE_GRID_KINDS.JOB_BOARD]: {
    fitMode: AG_GRID_FIT_MODES.COLUMNS,
    minWidth: 500,
    maxWidth: 1600,
    minHeight: 160,
    maxHeight: 760,
    fillMinFrameHeight: LARGE_TABLE_FILL_MIN_FRAME_HEIGHT,
    legacyMinFrameHeight: 340,
  },
  [WORKSPACE_GRID_KINDS.METRICS_TABLE]: {
    fitMode: AG_GRID_FIT_MODES.COLUMNS,
    minWidth: 1,
    maxWidth: 1120,
    minHeight: 84,
    maxHeight: 238,
    legacyMaxTableHeight: 260,
    legacyMinFrameHeight: 180,
  },
};

export function useWorkspaceGridLayout({
  columns,
  rows,
  kind = WORKSPACE_GRID_KINDS.SIGNAL_TABLE,
  workspace = false,
  mode = TABLE_MODES.COMPSHEET,
  headerHeight,
  groupHeaderHeight = 20,
  rowHeight,
  getRowHeight,
  pinnedRowHeight,
  includePinnedRows = false,
  pinnedRowCount,
  maxTableHeight,
  maxTableWidth,
  minContentTableHeight = 1,
  fitMode,
  workspaceFillMode = WORKSPACE_GRID_FILL_MODES.AUTO,
  panelWidthMode = WORKSPACE_GRID_PANEL_WIDTH_MODES.CONTENT,
  allowHorizontalScroll = false,
  mobileFrameHeight,
  legacy = {},
} = {}) {
  const config = KIND_CONFIG[kind] || KIND_CONFIG[WORKSPACE_GRID_KINDS.SIGNAL_TABLE];

  return useMemo(() => {
    const resolvedMaxWidth = maxTableWidth ?? config.maxWidth;
    const resolvedMaxHeight = maxTableHeight ?? config.maxHeight;
    const resolvedHeights = getModeHeights(mode);
    const resolvedFitMode = fitMode ?? config.fitMode;
    const resolvedContentMinHeight = workspace
      ? minContentTableHeight
      : config.minHeight;
    const tableSize = calculateAgGridTableSize({
      columnDefs: columns,
      rowData: rows,
      mode,
      includePinnedRows,
      pinnedRowCount,
      headerHeight: headerHeight ?? resolvedHeights.headerHeight,
      groupHeaderHeight,
      rowHeight,
      getRowHeight,
      pinnedRowHeight,
      fitMode: resolvedFitMode,
      minWidth: config.minWidth,
      maxWidth: resolvedMaxWidth,
      widthBuffer: 6,
      columnBorderReserve: 0,
      reserveScrollbar: false,
      minHeight: resolvedContentMinHeight,
      maxHeight: resolvedMaxHeight,
      heightBuffer: 4,
    });
    const panelWidth = typeof tableSize.width === 'number'
      ? tableSize.width + 12
      : tableSize.width;
    const shouldFillWorkspacePanel =
      workspaceFillMode === WORKSPACE_GRID_FILL_MODES.ALWAYS
      || (
        workspaceFillMode === WORKSPACE_GRID_FILL_MODES.AUTO
        && tableSize.shouldScroll
      );
    const workspaceGridHeight = shouldFillWorkspacePanel ? '100%' : tableSize.height;
    const fillContainerWidth = panelWidthMode === WORKSPACE_GRID_PANEL_WIDTH_MODES.CONTAINER;
    const workspacePanelWidth = fillContainerWidth ? '100%' : panelWidth;
    const workspaceGridWidth = allowHorizontalScroll
      ? tableSize.width
      : fillContainerWidth
        ? '100%'
        : tableSize.width;
    const fillMinFrameHeight = resolveFillMinFrameHeight(
      mobileFrameHeight ?? config.fillMinFrameHeight,
      config.minHeight
    );
    const horizontalScrollFrameSx = allowHorizontalScroll
      ? {
        alignItems: 'flex-start',
        overflowX: 'auto',
        overflowY: 'hidden',
        '& > *': {
          flex: '0 0 auto',
          minWidth: typeof tableSize.width === 'number' ? `${tableSize.width}px` : tableSize.width,
        },
      }
      : undefined;

    if (workspace) {
      return {
        tableSize,
        panelProps: {
          width: workspacePanelWidth,
          maxWidth: '100%',
          fill: shouldFillWorkspacePanel,
          minFrameHeight: shouldFillWorkspacePanel ? fillMinFrameHeight : tableSize.height,
          contentSx: DEFAULT_CONTENT_PADDING,
          frameSx: horizontalScrollFrameSx,
        },
        gridProps: {
          sizing: shouldFillWorkspacePanel ? AG_GRID_SIZING.LEGACY : AG_GRID_SIZING.CONTENT,
          fitMode: resolvedFitMode,
          minTableWidth: config.minWidth,
          maxTableWidth: resolvedMaxWidth,
          minTableHeight: shouldFillWorkspacePanel ? config.minHeight : resolvedContentMinHeight,
          maxTableHeight: resolvedMaxHeight,
          tableWidthBuffer: 0,
          tableHeightBuffer: 4,
          width: workspaceGridWidth,
          maxWidth: allowHorizontalScroll ? 'none' : '100%',
          height: workspaceGridHeight,
          maxHeight: workspaceGridHeight,
          ...(rowHeight ? { rowHeight } : {}),
        },
      };
    }

    return {
      tableSize,
      panelProps: {
        width: legacy.panelWidth ?? tableSize.width,
        maxWidth: legacy.panelMaxWidth ?? '100%',
        fill: legacy.panelFill ?? true,
        minFrameHeight: legacy.minFrameHeight ?? config.legacyMinFrameHeight,
        contentSx: legacy.contentSx,
      },
      gridProps: {
        sizing: AG_GRID_SIZING.LEGACY,
        maxTableHeight: legacy.maxTableHeight ?? config.legacyMaxTableHeight,
        width: legacy.gridWidth ?? tableSize.width,
        maxWidth: legacy.gridMaxWidth ?? '100%',
        height: legacy.gridHeight ?? '100%',
        maxHeight: legacy.gridMaxHeight ?? '100%',
      },
    };
  }, [
    columns,
    getRowHeight,
    config,
    fitMode,
    groupHeaderHeight,
    headerHeight,
    includePinnedRows,
    legacy.contentSx,
    legacy.gridHeight,
    legacy.gridMaxHeight,
    legacy.gridMaxWidth,
    legacy.gridWidth,
    legacy.maxTableHeight,
    legacy.minFrameHeight,
    legacy.panelFill,
    legacy.panelMaxWidth,
    legacy.panelWidth,
    minContentTableHeight,
    maxTableHeight,
    maxTableWidth,
    mobileFrameHeight,
    mode,
    panelWidthMode,
    pinnedRowCount,
    pinnedRowHeight,
    rowHeight,
    rows,
    workspace,
    allowHorizontalScroll,
    workspaceFillMode,
  ]);
}

export function WorkspaceGridPanel({
  layout = {},
  children,
  contentSx,
  fill,
  maxWidth,
  minFrameHeight,
  width,
  ...props
}) {
  const layoutPanelProps = layout.panelProps || {};
  const { contentSx: layoutContentSx, ...managedPanelProps } = layoutPanelProps;
  const hasManagedLayout = Boolean(layout.panelProps);
  const mergedContentSx = (layoutContentSx || contentSx)
    ? {
      ...(layoutContentSx || {}),
      ...(contentSx || {}),
    }
    : undefined;
  const sizingProps = hasManagedLayout
    ? managedPanelProps
    : {
      width,
      maxWidth,
      fill,
      minFrameHeight,
    };

  return (
    <AnalyticsTablePanel
      {...props}
      {...sizingProps}
      contentSx={mergedContentSx}
    >
      {children}
    </AnalyticsTablePanel>
  );
}

export const WorkspaceGrid = forwardRef(function WorkspaceGrid({
  layout,
  ...props
}, ref) {
  return (
    <AgGridWrapper
      ref={ref}
      surface="bare"
      {...layout.gridProps}
      {...props}
    />
  );
});
